/**
 * A calendar day is not an instant, and JavaScript disagrees.
 *
 * ── The bug this exists for ─────────────────────────────────────────
 *
 * `new Date('2026-08-26')` is **UTC midnight**, per the ECMAScript
 * spec: a date-only string is parsed as UTC while a date-TIME string
 * with no offset is parsed as local. So west of UTC, every date-only
 * value rendered with a locale formatter comes out as the day before:
 *
 *     TZ=America/New_York
 *     new Date('2026-08-26').toLocaleDateString()  →  "8/25/2026"
 *
 * An operator inspected a bridge this morning and the panel said the
 * inspection was YESTERDAY. Nothing failed, nothing logged, and the
 * record itself was right — only the reading of it was wrong, which
 * is the kind of thing somebody reports as "it saved the wrong date"
 * because from outside that is exactly what it looks like.
 *
 * ── There were three strategies for this, and one was missing ───────
 *
 * Same shape as the four grade baselines in CLAUDE.md: every surface
 * had solved it separately and they did not agree.
 *
 *   · `formatInspectionDate`, in three files, appended `T00:00:00`
 *     when the string was ten characters or fewer. Correct for
 *     `2026-08-26` and useless against the other shape below.
 *   · `formatWorkDate` rendered with `timeZone: 'UTC'`. Correct for
 *     both shapes, and wrong for a genuine timestamp, which it would
 *     print in UTC rather than where the reader is standing.
 *   · `formatWalkedOn` did neither, which is the one that was
 *     reported.
 *
 * ── Two shapes arrive, and both mean "a day" ────────────────────────
 *
 * Laravel's `'date'` cast serialises to `2026-08-26T00:00:00.000000Z`
 * and `'date:Y-m-d'` to `2026-08-26`. Both are calendar days; the
 * first merely looks like an instant. A hand-built response array can
 * emit the first even from a model whose cast says the second, because
 * the cast applies to the MODEL's serialisation and not to a Carbon
 * somebody put in an array — which is how the inspection endpoint came
 * to ship one.
 *
 * So both are accepted here. The UTC-midnight form is only ever read
 * as a day BY CALL SITE: a caller passes a column it knows to be a
 * date, and no general-purpose date parser in the app does this. A
 * real timestamp that happens to land exactly on UTC midnight is a
 * one-in-86400 coincidence and would be shifted by hours, never by a
 * day — but pass `inspected_at` here and you have made a mistake.
 */

/** `2026-08-26` — a plain calendar day. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `2026-08-26T00:00:00.000000Z` — a day wearing an instant's clothes. */
const UTC_MIDNIGHT = /^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.0+)?Z$/;

/**
 * A calendar day as a Date at LOCAL midnight.
 *
 * Local rather than UTC because everything downstream — the locale
 * formatter, `setHours`, a comparison against `new Date()` — works in
 * the reader's zone, and a value anchored anywhere else is one
 * arithmetic step from being off by a day.
 *
 * @param {string|Date|null|undefined} value
 * @returns {Date|null} null when the value is empty or unparseable
 */
export function parseCalendarDate(value) {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    const raw = String(value ?? '').trim();
    if (raw === '') return null;

    const m = DATE_ONLY.exec(raw) || UTC_MIDNIGHT.exec(raw);
    if (m) {
        // The three-argument form builds local midnight directly.
        // Month is zero-based, which is the other classic in this
        // family and the reason this is written once.
        return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    }

    // Anything else is a real instant — a photo's timestamp, an
    // `inspected_at`. Parsed as it is and rendered where the reader
    // is, which is correct for a moment in time.
    const dt = new Date(raw);

    return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * Local midnight today, for comparing days rather than moments.
 *
 * THE READER'S day, not the organization's, and that is a decision.
 * The absolute date is now right everywhere — that was the bug — but
 * the relative suffix ("Today", "Overdue 1 day") is measured against
 * the device. A manager in Auckland looking at a Californian org sees
 * the correct date with a suffix one day ahead of the crew's.
 *
 * Using the org's day would mean shipping it to the client, and the
 * field app runs offline for days at a time, where a stamp from the
 * last launch is worse than the clock in the reader's pocket. The
 * server's own `overdue` flag DOES use the org's day, so the badge is
 * authoritative and the suffix is a convenience.
 */
export function startOfToday() {
    const t = new Date();
    t.setHours(0, 0, 0, 0);

    return t;
}

/**
 * Whole days from today to `value`. Negative is the past.
 *
 * Both sides are floored to local midnight first, so "tomorrow" means
 * the next calendar day rather than twenty-four hours from now — an
 * inspection due tomorrow morning is not "in 0 days".
 *
 * @returns {number|null}
 */
export function calendarDaysFromToday(value) {
    const dt = parseCalendarDate(value);
    if (!dt) return null;
    const day = new Date(dt);
    day.setHours(0, 0, 0, 0);

    return Math.round((day - startOfToday()) / 86_400_000);
}

/**
 * A `Date` back to `YYYY-MM-DD`, read in the READER'S zone.
 *
 * The inverse of `parseCalendarDate`, and the reason it is here
 * rather than written inline at a call site: the obvious version is
 * `d.toISOString().slice(0, 10)`, and `toISOString` is UTC. Local
 * midnight on 26 August in California is `2026-08-26T07:00:00Z`,
 * which slices to the right day — and local midnight anywhere EAST
 * of Greenwich slices to the day before. A date picker built on it
 * sends the server a day the user did not click, in half the world,
 * which is this file's founding bug wearing a different hat.
 *
 * @param {Date|null|undefined} date
 * @returns {string|null}
 */
export function toCalendarDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    const pad = (n) => String(n).padStart(2, '0');

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** `Aug 26, 2026`, in the reader's locale. */
export function formatCalendarDate(value) {
    const dt = parseCalendarDate(value);
    if (!dt) return null;

    return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * A due date, with how far off it is.
 *
 * `Aug 26, 2026 · Overdue 3 days` / `· Today` / `· in 5 days`.
 */
export function formatDueDate(value, empty = '—') {
    const pretty = formatCalendarDate(value);
    if (!pretty) return value ? String(value) : empty;
    const days = calendarDaysFromToday(value);

    if (days < 0) return `${pretty} · Overdue ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'}`;
    if (days === 0) return `${pretty} · Today`;
    if (days === 1) return `${pretty} · Tomorrow`;
    if (days <= 14) return `${pretty} · in ${days} days`;

    return pretty;
}

/**
 * A date something HAPPENED on, with how long ago.
 *
 * `Aug 26, 2026 · Today` / `· Yesterday` / `· 5 days ago`.
 */
export function formatPastDate(value, empty = '—') {
    const pretty = formatCalendarDate(value);
    if (!pretty) return value ? String(value) : empty;
    const days = -calendarDaysFromToday(value);

    if (days <= 0) return `${pretty} · Today`;
    if (days === 1) return `${pretty} · Yesterday`;
    if (days <= 30) return `${pretty} · ${days} days ago`;

    return pretty;
}

/**
 * Is this due date in the past, or today?
 *
 * By CALENDAR DAY. The old version compared a parsed date against
 * `Date.now()`, so a structure due today read as overdue from the
 * moment UTC midnight passed — 20:00 the previous evening in New
 * York, which is a badge appearing on the wrong day.
 */
export function isOverdueDate(value) {
    const days = calendarDaysFromToday(value);

    return days !== null && days <= 0;
}

/**
 * TODAY, as `YYYY-MM-DD`, in the reader's zone.
 *
 * `toCalendarDate(startOfToday())` is two calls and it was written as
 * `new Date().toISOString().slice(0, 10)` in NINE places instead —
 * every Day / Week / Month view in Expenses and Work hours, the daily
 * report, and the field app's time-log form. Measured, that is wrong
 * for a third of the day in both directions: at UTC-7 it answers
 * TOMORROW from 5pm, and at UTC+13 it answers YESTERDAY until 1pm.
 *
 * It is here rather than left as a compose because the wrong version
 * is one line, reads as obviously correct, and is the single most
 * copied expression in this codebase.
 */
export function todayCalendarDate() {
    return toCalendarDate(startOfToday());
}

/**
 * Today in a named IANA zone, as `YYYY-MM-DD` — an ORGANIZATION's day
 * rather than the reader's. Null when the zone is missing or the
 * runtime does not know it, so a caller can fall back on purpose.
 *
 * `en-CA` is not a locale choice: it is the one whose date format is
 * already `YYYY-MM-DD`. Parts are read by type anyway, so a runtime
 * that formats it differently still answers correctly.
 */
export function todayInZone(timeZone, now = new Date()) {
    if (! timeZone) return null;
    try {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
        }).formatToParts(now);
        const get = (t) => parts.find((p) => p.type === t)?.value;
        const y = get('year'); const m = get('month'); const d = get('day');
        return y && m && d ? `${y}-${m}-${d}` : null;
    } catch {
        return null;
    }
}

/**
 * `value` moved by `n` whole calendar days, back as `YYYY-MM-DD`.
 *
 * Whole DAYS, not 24-hour steps: the arithmetic runs on a local
 * midnight `Date`, and `setDate` crosses a daylight-saving boundary
 * correctly where adding 86 400 000 ms does not. That matters twice a
 * year on exactly one step, which is the kind of bug nobody
 * reproduces.
 *
 * @returns {string|null} null when `value` is not a date
 */
export function addCalendarDays(value, n) {
    const d = parseCalendarDate(value);
    if (!d) return null;
    const out = new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

    return toCalendarDate(out);
}

/**
 * The Monday of `value`'s week, as `YYYY-MM-DD`.
 *
 * Monday because that is what the server's week views are cut on —
 * `startOfWeek(Carbon::MONDAY)` — and a client that disagreed would
 * ask for a window nothing is grouped by. This is deliberately NOT
 * the locale's first day, which `DateRangePicker` does use: the
 * picker draws a calendar for a human to read, and this addresses a
 * stored period.
 *
 * @returns {string|null}
 */
export function startOfWeekMonday(value) {
    const d = parseCalendarDate(value);
    if (!d) return null;
    // getDay(): 0 is Sunday, so Sunday steps back six rather than
    // forward one — the off-by-one that makes a Sunday show next
    // week.
    const day = d.getDay();

    return addCalendarDays(toCalendarDate(d), day === 0 ? -6 : 1 - day);
}
